import {useEffect, useMemo, useState} from "react";
import {Box, Button, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";
import CloseIcon from "@mui/icons-material/Close";
import type {ProfileFieldDefinition, ProfileFieldValues} from "../../../../../sdo/ProfileFieldDefinition.ts";
import {fieldSx, selectSx} from "../resolvers/editors/ResolverUtils.tsx";
import {hintColor} from "../../../../../AppStyles.ts";

interface ProfileDetailsFormProps {
    fields: ProfileFieldDefinition[];
    values: ProfileFieldValues;
    editable: boolean;
    onSave?: (values: ProfileFieldValues) => void;
    // When provided, replaces the Revert button with a Cancel button (create-mode use case).
    onCancel?: () => void;
    // Override the default Save button label (e.g. "Create Profile").
    saveLabel?: string;
    // When true, Save is enabled regardless of dirty state (used by Create since there's no "saved" baseline).
    alwaysEnableSave?: boolean;
}

// Multiline-friendly variant of fieldSx (used for TEXT data type)
const multilineFieldSx = {
    ...fieldSx,
    "& .MuiOutlinedInput-root": {
        ...(fieldSx as Record<string, unknown>)["& .MuiOutlinedInput-root"] as object,
        height: "auto",
        padding: "10px 12px",
    },
    "& .MuiOutlinedInput-input": {
        color: "#f2f5fa",
        fontSize: "0.80rem",
        lineHeight: 1.6,
    },
};

const requiredLabelSx = {
    "& .MuiInputLabel-root .MuiFormLabel-asterisk": {color: hintColor},
};

const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": {color: "#38ff82"},
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {backgroundColor: "rgba(56, 255, 130, 0.5)"},
    "& .MuiSwitch-track": {backgroundColor: "rgba(112, 147, 160, 0.4)"},
};

function valuesEqual(a: ProfileFieldValues, b: ProfileFieldValues): boolean {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) {
        if ((a[k] ?? "") !== (b[k] ?? "")) return false;
    }
    return true;
}

function formatReadOnlyValue(field: ProfileFieldDefinition, raw: string): string {
    if (raw == null || raw === "") return "—";
    if (field.dataType === "BOOLEAN") {
        return raw === "true" ? "Yes" : "No";
    }
    if (field.dataType === "LIST_ENTRY" && field.options) {
        const match = field.options.find((o) => o.key === raw);
        return match ? match.label : raw;
    }
    return raw;
}

function ProfileDetailsForm({fields, values, editable, onSave, onCancel, saveLabel, alwaysEnableSave}: ProfileDetailsFormProps) {
    const [draft, setDraft] = useState<ProfileFieldValues>(values);

    // Re-sync draft if the parent reloads values (e.g., after a successful save or profile reload)
    useEffect(() => {
        setDraft(values);
    }, [values]);

    const sortedFields = useMemo(
        () => [...fields].sort((a, b) => a.position - b.position),
        [fields],
    );

    const isDirty = !valuesEqual(draft, values);

    const upd = (key: string, value: string) => {
        setDraft((prev) => ({...prev, [key]: value}));
    };

    const handleRevert = () => {
        setDraft(values);
    };

    const handleSave = () => {
        // TODO: Validate required fields before save (mirrors desktop settingsForm.getForm().validate())
        if (onSave) onSave(draft);
    };

    const renderField = (field: ProfileFieldDefinition) => {
        const value = draft[field.key] ?? "";

        // Editable mode dispatches by dataType
        switch (field.dataType) {
            case "BOOLEAN":
                return (
                    <FormControlLabel
                        key={field.key}
                        control={
                            <Switch
                                checked={value === "true"}
                                onChange={(e) => upd(field.key, e.target.checked ? "true" : "false")}
                                sx={switchSx}
                            />
                        }
                        label={
                            <Typography sx={{color: "#f2f5fa", fontSize: "0.85rem"}}>
                                {field.label}{field.required ? " *" : ""}
                                {field.hintText && (
                                    <Typography component="span" sx={{
                                        color: "rgba(242, 245, 250, 0.45)",
                                        fontSize: "0.72rem",
                                        ml: 1,
                                        fontStyle: "italic",
                                    }}>
                                        {field.hintText}
                                    </Typography>
                                )}
                            </Typography>
                        }
                        sx={{ml: 0}}
                    />
                );

            case "LIST_ENTRY":
                return (
                    <TextField
                        key={field.key}
                        select
                        label={field.label}
                        value={value}
                        onChange={(e) => upd(field.key, e.target.value)}
                        helperText={field.hintText || undefined}
                        required={field.required}
                        size="small"
                        fullWidth
                        sx={{...selectSx, ...requiredLabelSx, ...helperTextSx}}
                    >
                        <MenuItem value="">
                            <em style={{color: "rgba(242, 245, 250, 0.4)"}}>— None —</em>
                        </MenuItem>
                        {(field.options || []).map((opt) => (
                            <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
                        ))}
                    </TextField>
                );

            case "DATE":
                return (
                    <TextField
                        key={field.key}
                        type="date"
                        label={field.label}
                        value={value}
                        onChange={(e) => upd(field.key, e.target.value)}
                        helperText={field.hintText || undefined}
                        required={field.required}
                        size="small"
                        fullWidth
                        slotProps={{inputLabel: {shrink: true}}}
                        sx={{...fieldSx, ...requiredLabelSx, ...helperTextSx}}
                    />
                );

            case "DATETIME":
                return (
                    <TextField
                        key={field.key}
                        type="datetime-local"
                        label={field.label}
                        value={value}
                        onChange={(e) => upd(field.key, e.target.value)}
                        helperText={field.hintText || undefined}
                        required={field.required}
                        size="small"
                        fullWidth
                        slotProps={{inputLabel: {shrink: true}}}
                        sx={{...fieldSx, ...requiredLabelSx, ...helperTextSx}}
                    />
                );

            case "TIME":
                return (
                    <TextField
                        key={field.key}
                        type="time"
                        label={field.label}
                        value={value}
                        onChange={(e) => upd(field.key, e.target.value)}
                        helperText={field.hintText || undefined}
                        required={field.required}
                        size="small"
                        fullWidth
                        slotProps={{inputLabel: {shrink: true}}}
                        sx={{...fieldSx, ...requiredLabelSx, ...helperTextSx}}
                    />
                );

            case "TEXT":
                return (
                    <TextField
                        key={field.key}
                        label={field.label}
                        value={value}
                        onChange={(e) => upd(field.key, e.target.value)}
                        helperText={field.hintText || undefined}
                        required={field.required}
                        size="small"
                        fullWidth
                        multiline
                        rows={5}
                        sx={{...multilineFieldSx, ...requiredLabelSx, ...helperTextSx}}
                    />
                );

            case "INTEGER":
            case "DOUBLE":
                return (
                    <TextField
                        key={field.key}
                        type="number"
                        label={field.label}
                        value={value}
                        onChange={(e) => upd(field.key, e.target.value)}
                        helperText={field.hintText || undefined}
                        required={field.required}
                        size="small"
                        fullWidth
                        sx={{...fieldSx, ...requiredLabelSx, ...helperTextSx}}
                    />
                );

            case "STRING":
            default:
                return (
                    <TextField
                        key={field.key}
                        label={field.label}
                        value={value}
                        onChange={(e) => upd(field.key, e.target.value)}
                        helperText={field.hintText || undefined}
                        required={field.required}
                        size="small"
                        fullWidth
                        sx={{...fieldSx, ...requiredLabelSx, ...helperTextSx}}
                    />
                );
        }
    };

    // Read-only mode: each key/value on its own full-width row, stacked vertically.
    // Borrows the pill background/border from SummaryPill but stretches edge-to-edge
    // within the form's max-width container.
    if (!editable) {
        return (
            <Stack direction="column" spacing={1} sx={{
                width: "100%",
                maxWidth: 760,
                mx: "auto",
                padding: "8px 4px",
                alignItems: "stretch",
            }}>
                {sortedFields.map((field) => (
                    <Box
                        key={field.key}
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "flex-start",
                            gap: 1.5,
                            width: "100%",
                            padding: "10px 16px",
                            borderRadius: "10px",
                            border: "1px solid rgba(112, 147, 160, 0.35)",
                            backgroundColor: "rgba(9, 25, 32, 0.6)",
                        }}
                    >
                        <Typography sx={{
                            color: "#a8b8c0",
                            fontSize: "0.70rem",
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            textTransform: "uppercase",
                            textAlign: "left",
                            whiteSpace: "nowrap",
                            minWidth: "140px",
                            paddingTop: "2px",
                        }}>
                            {field.label}{field.required ? " *" : ""}
                        </Typography>
                        <Typography sx={{
                            color: "#ffffff",
                            fontSize: "0.80rem",
                            flex: 1,
                            textAlign: "left",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }}>
                            {formatReadOnlyValue(field, draft[field.key] ?? "")}
                        </Typography>
                    </Box>
                ))}
            </Stack>
        );
    }

    return (
        <Stack direction="column" spacing={1.5} sx={{
            width: "100%",
            maxWidth: 760,
            mx: "auto",
            padding: "8px 4px",
        }}>
            {sortedFields.map(renderField)}

            {/* Save + (Cancel | Revert) action bar (only when editable) */}
            {editable && (
                <Box sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    pt: 2,
                    mt: 1,
                    borderTop: "1px solid rgba(63, 70, 79, 0.4)",
                }}>
                    {onCancel ? (
                        <Button
                            onClick={onCancel}
                            startIcon={<CloseIcon/>}
                            sx={{
                                color: "#7093a0",
                                fontSize: "0.80rem",
                                "&:hover": {color: hintColor},
                            }}
                        >
                            Cancel
                        </Button>
                    ) : (
                        <Button
                            onClick={handleRevert}
                            disabled={!isDirty}
                            startIcon={<RestoreIcon/>}
                            sx={{
                                color: "#7093a0",
                                fontSize: "0.80rem",
                                "&:disabled": {color: "rgba(112, 147, 160, 0.3)"},
                            }}
                        >
                            Revert
                        </Button>
                    )}
                    <Button
                        onClick={handleSave}
                        disabled={!alwaysEnableSave && !isDirty}
                        variant="contained"
                        startIcon={<SaveIcon/>}
                        sx={{
                            backgroundColor: "rgba(56, 255, 130, 0.15)",
                            color: "#38ff82",
                            border: "1px solid rgba(56, 255, 130, 0.5)",
                            fontSize: "0.80rem",
                            textTransform: "none",
                            "&:hover": {
                                backgroundColor: "rgba(56, 255, 130, 0.25)",
                                border: "1px solid #38ff82",
                            },
                            "&:disabled": {
                                backgroundColor: "rgba(56, 255, 130, 0.05)",
                                color: "rgba(56, 255, 130, 0.3)",
                                border: "1px solid rgba(56, 255, 130, 0.15)",
                            },
                        }}
                    >
                        {saveLabel ?? "Save Changes"}
                    </Button>
                </Box>
            )}
        </Stack>
    );
}

// Helper text styling convention (pink italic right-aligned, per §15)
const helperTextSx = {
    "& .MuiFormHelperText-root": {
        color: hintColor,
        fontSize: "0.65rem",
        fontStyle: "italic",
        textAlign: "right" as const,
        marginLeft: 0,
        marginRight: "4px",
        marginTop: "4px",
    },
};

export default ProfileDetailsForm;
